/**
 * Pure (platform-neutral) parsing for the structured CvDiff payload a skill run
 * returns. Mirrors the tolerant reader in `skill-ui.web.tsx` so the native run
 * sheet and the native skill tool-cards can render the same diff without pulling
 * in the web-only JSX module. Anything it doesn't recognise is ignored.
 */

export interface SkillSectionEdit {
  section: string;
  field?: string;
  before?: string;
  after: string;
  rationale?: string;
}

export interface SkillGroupChange {
  category: string;
  items: string[];
}

export interface ParsedSkillDiff {
  outputText?: string;
  title?: string;
  headline?: string;
  summary?: string;
  changeSummary: string[];
  sectionEdits: SkillSectionEdit[];
  skillGroups: SkillGroupChange[];
  nextActions: string[];
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter(
        (v): v is string => typeof v === 'string' && v.trim().length > 0,
      )
    : [];
}

function asSectionEdits(value: unknown): SkillSectionEdit[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const e = entry as Record<string, unknown>;
    const after = asString(e.after);
    const section = asString(e.section);
    if (!after || !section) return [];
    return [
      {
        section,
        field: asString(e.field) ?? undefined,
        before: asString(e.before) ?? undefined,
        after,
        rationale: asString(e.rationale) ?? undefined,
      },
    ];
  });
}

function asSkillGroups(value: unknown): SkillGroupChange[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const e = entry as Record<string, unknown>;
    const category = asString(e.category);
    const items = asStringArray(e.items);
    if (!category) return [];
    return [{ category, items }];
  });
}

/** Normalise the raw diff record into the structured shape the UI renders. */
export function parseSkillDiff(
  diff: Record<string, unknown> | undefined,
): ParsedSkillDiff {
  const d = diff ?? {};
  return {
    outputText: asString(d.outputText) ?? undefined,
    title: asString(d.title) ?? undefined,
    headline: asString(d.headline) ?? undefined,
    summary: asString(d.summary) ?? undefined,
    changeSummary: asStringArray(d.changeSummary),
    sectionEdits: asSectionEdits(d.sectionEdits),
    skillGroups: asSkillGroups(d.skillGroups),
    nextActions: asStringArray(d.nextActions),
  };
}

/**
 * The structured section content a `kind: cv_custom` run writes.
 *
 * `buildCvCustomDiff` (apps/api) emits these arrays instead of the narrative
 * `changeSummary` / `sectionEdits` a default CV skill produces, and its
 * `changeSummary` / `nextActions` are OPTIONAL in the model-facing schema. So a
 * cv_custom skill that rewrote every experience bullet can return a diff with
 * none of the fields {@link parseSkillDiff} reads - which made the run screens
 * announce "No changes needed" over a CV that had just been rewritten.
 *
 * Shared so the native (`skill-diff`) and web (`skill-ui.web`) emptiness checks
 * cannot drift.
 */
export const CV_CUSTOM_CONTENT_FIELDS = [
  'experiences',
  'educations',
  'certifications',
  'languages',
  'projects',
  'highlights',
] as const;

/** True when a diff carries structured cv_custom section content. */
export function hasCvCustomContent(
  diff: Record<string, unknown> | undefined,
): boolean {
  if (!diff) return false;
  return CV_CUSTOM_CONTENT_FIELDS.some((field) => {
    const value = diff[field];
    return Array.isArray(value) && value.length > 0;
  });
}

/** True when the diff carries nothing worth showing. */
export function skillDiffIsEmpty(
  diff: Record<string, unknown> | undefined,
): boolean {
  const parsed = parseSkillDiff(diff);
  return (
    parsed.changeSummary.length === 0 &&
    parsed.sectionEdits.length === 0 &&
    parsed.skillGroups.length === 0 &&
    parsed.nextActions.length === 0 &&
    !parsed.outputText &&
    !parsed.title &&
    !parsed.headline &&
    !parsed.summary &&
    !hasCvCustomContent(diff)
  );
}

/** Pull the diff record out of a run result (handles `result.diff` and `result.run.diff`). */
export function extractRunDiff(
  result: unknown,
): Record<string, unknown> {
  if (!result || typeof result !== 'object') return {};
  const r = result as Record<string, unknown>;
  if (r.diff && typeof r.diff === 'object' && !Array.isArray(r.diff)) {
    return r.diff as Record<string, unknown>;
  }
  const run = r.run;
  if (run && typeof run === 'object') {
    const runDiff = (run as Record<string, unknown>).diff;
    if (runDiff && typeof runDiff === 'object' && !Array.isArray(runDiff)) {
      return runDiff as Record<string, unknown>;
    }
  }
  return {};
}

/** Return the safe user-facing failure carried by an audited skill tool. */
export function extractSkillRunFailure(params: {
  result: unknown;
  stage?: string;
  isError?: boolean;
}): string | null {
  const root =
    params.result &&
    typeof params.result === 'object' &&
    !Array.isArray(params.result)
      ? (params.result as Record<string, unknown>)
      : null;
  const failed =
    params.isError ||
    params.stage === 'failed' ||
    params.stage === 'declined' ||
    root?.ok === false;
  if (!failed) return null;

  const error =
    root?.error && typeof root.error === 'object' && !Array.isArray(root.error)
      ? (root.error as Record<string, unknown>)
      : null;
  const message = error?.message ?? root?.message;
  return typeof message === 'string' && message.trim().length > 0
    ? message.trim()
    : '';
}
